import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { positiveIntId } from '@/lib/schemas';
import { useEmotionQuestion } from '@/hooks/queries';
import { emotionTopicFor } from '@/lib/emotionTopic';
import { useAuthStore } from '@/auth';
import type { ClinicianFlag } from '@/hooks/useClinicianFlags';
import { RxAssistantChat } from '@/components/RxAssistantChat';
import { useRxAssistantMode } from '@/hooks/useRxAssistantMode';
import { cn } from '@/lib/utils';
import { PatientTabNav, type PatientTab } from './components/PatientTabNav';
import { PatientHeaderBar } from './components/PatientHeaderBar';
import { PatientContextRow } from './components/PatientContextRow';
import { DateSelectionToolbar } from './components/DateSelectionToolbar';
import { NutrientDistributionPrefetch } from './components/NutrientDistributionCards';
import { HeaderLeftPortal } from '@/components/HeaderLeftPortal';
import { HeaderBelowPortal } from '@/components/HeaderBelowPortal';
import { HeaderActionsPortal } from '@/components/HeaderActionsPortal';
import { RequiringActionIndicator } from './components/RequiringActionIndicator';
import { useIdleReady } from '@/hooks/useIdleReady';
import { isoRangeFromSearchParams, useDateRange } from '@/hooks/useDateRange';

/**
 * Patient route shell. Two responsibilities only:
 *   1. Render the horizontal sub-nav (icon + label per tab).
 *   2. Mount the matching tab via the existing nested routes.
 *
 * The patient identity (name, demographics, action buttons) lives in the
 * global fixed header via PatientHeaderBar. The patient details panel (email,
 * dates, Goals, Requiring Action, Medications) lives in a TOP OVERLAY DRAWER
 * opened from the "Patient details" button in the tab-nav row — freeing the
 * shell to a clean two-panel layout: tab content + (optional) docked assistant.
 *
 * RxAssistant defaults to popper mode (set via the useRxAssistantMode hook's
 * persistence) so the layout isn't fighting a docked column for space.
 */

const Summary = lazy(() => import('./tabs/Summary'));
const LogsV1 = lazy(() => import('./tabs/LogsV1'));
const LogsV2 = lazy(() => import('./tabs/LogsV2'));
const LogsV3 = lazy(() => import('./tabs/LogsV3'));
const Nutrients = lazy(() => import('./tabs/Nutrients'));
const Goals = lazy(() => import('./tabs/Goals'));
const Recipes = lazy(() => import('./tabs/Recipes'));
const MealPlans = lazy(() => import('./tabs/MealPlans'));
const Wellbeing = lazy(() => import('./tabs/Wellbeing'));
const Reports = lazy(() => import('./tabs/Reports'));
const Documents = lazy(() => import('./tabs/Documents'));
const Subscriptions = lazy(() => import('./tabs/Subscriptions'));

type TabDef = PatientTab & {
  element: React.LazyExoticComponent<(props: { patientId: number }) => JSX.Element>;
  flag?: ClinicianFlag;
  /** When true, the route is mounted with `${to}/*` so the tab can render
   *  its own nested router (e.g. Meal Plans → list / intake / viewer). */
  nestedRoutes?: boolean;
  /** Hidden from the tab nav but still routable (deep links keep working). */
  hidden?: boolean;
};

const TABS: TabDef[] = [
  { to: 'summary', label: 'Summary', element: Summary },
  // Meals (Logs v2) is the primary food-log view. Logs v1 + v3 are hidden
  // from the nav but stay routable so existing deep links keep working.
  { to: 'logs', label: 'Logs v1', element: LogsV1, hidden: true },
  { to: 'logs-v2', label: 'Meals', element: LogsV2 },
  { to: 'logs-v3', label: 'Logs v3', element: LogsV3, hidden: true },
  { to: 'nutrients', label: 'Nutrients', element: Nutrients },
  { to: 'prescriptions', label: 'Goals', element: Goals },
  // Recipes tab stays on clinician_meal_planner_enabled (unchanged). The new
  // clinic_use_recipes flag only unlocks the managed library *section* inside
  // the tab — it must not gate or replace this existing surface.
  { to: 'recipes', label: 'Recipes', element: Recipes, flag: 'clinician_meal_planner_enabled' },
  {
    to: 'meal-plans',
    label: 'Meal Plans',
    element: MealPlans,
    flag: 'clinician_meal_planner_enabled',
    nestedRoutes: true,
  },
  // Wellbeing — combines the existing mood trend with mindful-eating
  // reflections (gated by clinic flag). Route stays `emotions` for
  // backward compatibility with bookmarks and the dynamic tab label
  // logic below; the displayed label can flex between
  // "Wellbeing"/"Digestion"/"Emotions" depending on the patient's
  // emotion question topic.
  { to: 'emotions', label: 'Wellbeing', element: Wellbeing },
  { to: 'reports', label: 'Reports', element: Reports },
  { to: 'documents', label: 'Documents', element: Documents },
  { to: 'subscriptions', label: 'Logging Periods', element: Subscriptions },
];

export default function PatientShell() {
  // OWASP A03 — validate the route param BEFORE any API calls. Outer
  // component intentionally calls no hooks beyond `useParams` so the
  // early Navigate doesn't violate rules-of-hooks.
  const { id } = useParams();
  const parsed = positiveIntId.safeParse(id);
  if (!parsed.success) return <Navigate to="/patients" replace />;
  return <PatientShellBody patientId={parsed.data} />;
}

function PatientShellBody({ patientId }: { patientId: number }) {
  const { t } = useTranslation();
  const emotionQuestion = useEmotionQuestion(patientId);
  const emotionTopic = emotionTopicFor(emotionQuestion.data?.question);
  // Tab label flexes: "Digestion" for patients whose emotion question
  // is a digestion topic, otherwise "Wellbeing" (which now covers the
  // mood trend AND mindful-eating reflections).
  const emotionsTabLabel = emotionTopic === 'Digestion' ? 'Digestion' : 'Wellbeing';

  const rxAssistant = useRxAssistantMode();

  // Derive the current tab key from the route so the assistant chip
  // rack can match the active page's registered context. Tab routes
  // are `/patients/{id}/{tab}[/...]` — extract the segment after the
  // patient id. Tabs that haven't registered a context just won't
  // show chips (the rack returns null on empty).
  const location = useLocation();
  const contextKey = useMemo(() => {
    const m = location.pathname.match(/^\/patients\/\d+\/([^/]+)/);
    return m ? m[1] : undefined;
  }, [location.pathname]);

  // Shared sticky date bar — same URL-synced range every time-scoped tab used
  // to render in its own card. Recipes / Meal Plans / Logging Periods are not
  // time-scoped and hide the bar.
  const TIME_SCOPED_TABS = useMemo(
    () =>
      new Set([
        'summary',
        'logs',
        'logs-v2',
        'logs-v3',
        'nutrients',
        'prescriptions',
        'emotions',
        'reports',
      ]),
    [],
  );
  const showDateBar = TIME_SCOPED_TABS.has(contextKey ?? '');
  const sharedDateRange = useDateRange(patientId);

  // Warm the Summary distribution graphs (the heavy disaggregated per-meal
  // fan-out) during browser idle ONLY, so it never competes with the active
  // view — critical for slow/older machines. Read-only URL range (never writes
  // params); skip when the URL carries no range yet (fresh open — Summary is
  // the default tab and loads it on demand, then sets the URL range so later
  // tab hops get pre-warmed).
  const [searchParams] = useSearchParams();
  const prefetchRange = useMemo(() => isoRangeFromSearchParams(searchParams), [searchParams]);
  const idleReady = useIdleReady();

  // Two-stage flag filter so deep-link F5s on flag-gated tabs survive:
  //   1. `clinicianLoaded === false` — include ALL tabs so the route table
  //      covers the current URL; otherwise the catch-all redirects deep
  //      links before the flag query resolves.
  //   2. `clinicianLoaded === true` — apply the real flag filter.
  const clinician = useAuthStore((s) => s.clinician);
  const clinicianLoaded = clinician !== null;
  const flags = clinician?.flags as Record<string, unknown> | undefined;
  const visibleTabs = useMemo(
    () =>
      TABS.filter(
        (tab) => !tab.flag || !clinicianLoaded || flags?.[tab.flag] === true,
      ),
    [flags, clinicianLoaded],
  );

  const assistantEnabled =
    flags?.clinic_enable_clinician_advisor_chat === true;

  // Viewport flag for the 3-col layout (rail + content + dedicated
  // assistant column). At 2xl (1536px+) we have room for a third
  // grid track. Between xl and 2xl, the docked assistant has to share
  // a column with the rail — we stack it under the rail rather than
  // letting it wrap below the entire tab content.
  const [is2xlViewport, setIs2xlViewport] = useState<boolean>(() =>
    typeof window === 'undefined' ? true : window.matchMedia('(min-width: 1536px)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1536px)');
    const onChange = () => setIs2xlViewport(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // RxAssistant docking just needs the 2xl 3-track layout. Collapsing the left
  // rail is a way to FREE room for the docked panel + content (the freed ~232px
  // flows into the content column), so it must NOT undock the assistant — that
  // was the reverse of what a clinician tightening the layout wants. Undock only
  // when the viewport itself is too narrow for the 3rd track; the stored
  // preference (`useRxAssistantMode`) is untouched, so widening restores it.
  const dockHasRoom = is2xlViewport;
  const effectiveAssistantMode =
    rxAssistant.effectiveMode === 'panel' && !dockHasRoom
      ? 'popper'
      : rxAssistant.effectiveMode;
  const assistantDocked = assistantEnabled && effectiveAssistantMode === 'panel';

  return (
    /* Shell-level grid — the two-panel target:
         [ tab nav + tab content | optional docked assistant ]
       The patient details panel is no longer a column; it's the top overlay
       drawer opened from the tab-nav row. */
    <div
      className={cn(
        'grid items-start gap-4 grid-cols-[minmax(0,1fr)]',
        // The assistant's 2nd track only mounts when it's actually docked (2xl);
        // otherwise the assistant is a floating popper and needs no column.
        assistantDocked && '2xl:grid-cols-[minmax(0,1fr)_380px]',
      )}
    >
      {/* Patient identity (name + id + profile actions) lives in the global
          fixed header — injected via the header portal so it stays visible
          while everything below scrolls. */}
      <HeaderLeftPortal>
        <PatientHeaderBar patientId={patientId} />
      </HeaderLeftPortal>

      {/* "Requiring action" indicator in the header action cluster (beside the
          Invitation code button): a static "all clear" pill when there's
          nothing, an amber count that expands the list when there is. */}
      <HeaderActionsPortal>
        <RequiringActionIndicator patientId={patientId} />
      </HeaderActionsPortal>

      {/* Patient clinical context under the name — motivations, restrictions,
          start/last report, clinic. Email dropped from the always-on strip. */}
      <HeaderBelowPortal>
        <PatientContextRow patientId={patientId} />
      </HeaderBelowPortal>

      {/* Tab nav + shared date bar + content. */}
      <div className="min-w-0 space-y-4">
        <div className="sticky -top-4 z-20 -mt-4 space-y-1.5 bg-background pt-4 lg:-top-6 lg:-mt-6 lg:pt-6">
          <PatientTabNav
            patientId={patientId}
            tabs={visibleTabs.filter((tab) => !tab.hidden)}
            emotionsLabel={t(emotionsTabLabel)}
          />
          {showDateBar && (
            <DateSelectionToolbar
              patientId={patientId}
              value={sharedDateRange}
              variant="bare"
              title={null}
            />
          )}
        </div>
        {idleReady && prefetchRange && (
          <NutrientDistributionPrefetch patientId={patientId} range={prefetchRange} />
        )}
        <ErrorBoundary>
          <Suspense
            fallback={
              <div className="space-y-3" data-testid="tab-loading">
                <Skeleton className="h-6 w-1/3" />
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            }
          >
            <Routes>
              {/* Absolute paths — v6's relative splat resolution caused
                  infinite-append loops with `to="summary"`. */}
              <Route
                index
                element={<Navigate to={`/patients/${patientId}/summary`} replace />}
              />
              {visibleTabs.map((tab) => {
                const C = tab.element;
                const path = tab.nestedRoutes ? `${tab.to}/*` : tab.to;
                return (
                  <Route
                    key={tab.to}
                    path={path}
                    element={<C patientId={patientId} />}
                  />
                );
              })}
              <Route
                path="*"
                element={<Navigate to={`/patients/${patientId}/summary`} replace />}
              />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </div>

      {/* RxAssistant — popper / panel / closed. Gated by the clinician's
          `clinic_enable_clinician_advisor_chat` flag. Position:fixed when
          popper/closed (so the grid track for it stays empty + unused);
          docked into the grid's right track when mode='panel'. */}
      {assistantEnabled && (
        // When docked, stretch the assistant's grid cell to the full row
        // height (overriding the grid's items-start) so the panel's inner
        // sticky wrapper has room to stay pinned while the tab content scrolls
        // past on the left. In popper/closed mode the content is fixed-position
        // so the (empty) cell has no visual effect.
        <div className={cn(assistantDocked && 'flex flex-col self-stretch')}>
          <RxAssistantChat
            patientId={patientId}
            mode={effectiveAssistantMode}
            onModeChange={rxAssistant.setMode}
            contextKey={contextKey}
          />
        </div>
      )}
    </div>
  );
}
